import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  fetchReportList,
  fetchReportById,
  createNewReport,
  addNewReport,
  deleteReport,
  deleteBatch,
  renameReport,
  mergeReports,
  fetchMergeableReports,
  createBatchReport,
  generateSummaryRequest,
  updateSummaryRequest,
  getSummaryRequest,
} from "~/services/gateways/report.gateway";
import { pdfGateway } from "_@snap/reports/services/gateways/pdf.gateway";
import { docxGateway } from "_@snap/reports/services/gateways/docx.gateway";
import { SummaryOutputDTO } from "root/domain/http/dto/report/summary.output";
import { base64ToUint8Array, useEncryption } from "~/hooks/useEncryption";
import { useEnvelopeEncryption } from "~/hooks/useEnvelopeEncryption";
import { recordCreateIssued } from "~/helpers/perceivedLatency.helper";
import type {
  EncryptedPayload,
  ReportData,
  ReportPDFProps,
  NewReportRequest,
  MergeReportsPayload,
  MergeReportsProps,
  BatchReportRequest,
  MergeableReportItem,
  SummaryResponse,
} from "~/types/global";
import { FolderData } from "root/domain/entities/folder.model";
import { toast } from "sonner";
import { useDialogActions } from "~/store/dialogStore";
import { useNewPendingReportsActions } from "~/store/newReportStatusStore";
import {
  decryptReportPayload,
  encryptReportPayload,
  combineReportSearchArgs,
  combineReportSections,
  buildReportSourcesUsed,
  normalizeDocument,
} from "~/helpers";
import { REPORT_CONSTANTS, tanstackQueryConfig } from "~/helpers/constants";
import { emitEvent } from "~/helpers/telemetry.helper";
import { recordZeroResultListSearch } from "~/helpers/searchToCreationCorrelation.helper";
import {
  useReportListLoadCount,
  useReportListPage,
  useReportListActions,
  useReportListSearchFilter,
  useReportListStore,
} from "~/store/reportListStore";
import { createErrorHandler } from "~/helpers/errorHandling.helper";
import { decryptListItem } from "~/helpers/item-decryption.helper";
import { useIsPasswordExpired } from "~/store/credentials";
import { usePasswordCheck } from "~/hooks/usePasswordCheck";
import { usePassword } from "~/store/credentials";
import { useUserActions, useUserCredits } from "~/store/userStore";
import { type UpdateSummaryInput } from "root/domain/http/dto/report/summary.input";
import { usePermissionManager } from "~/services/permissionManager";
import { getUserAiModel } from "~/store/userStore";
import { unwrapDekFromUser } from "~/helpers/envelopeCrypto";
import { isPendingReport } from "~/helpers/reportStatus.helper";
import { resolveReportDetailsPollMs } from "~/helpers/report-details-refetch.helper";
import { useReportProgressStore } from "~/store/reportProgressStore";
import { useIsReportStatusSocketConnected } from "~/store/reportStatusSocketStore";

import { useEnvelopeAwareDecryptor } from "~/hooks/useEnvelopeAwareDecryptor";
import { useIsMigrationPending } from "~/store/legacyMigrationStore";
import { useFavoritesStore } from "~/store/favoritesStore";
import {
  buildReportSearchArgsNgramInput,
  extractRedesSociaisEntryValue,
  extractRedesSociaisFallback,
  extractRedesSociaisSafeFields,
  isRedesSociaisType,
  reconcileRedesSociaisCompletion,
  REDES_SOCIAIS_IGNORE_KEY,
} from "root/modules/@snap/reports/helpers/redes-sociais-search-args.helpers";

/**
 * `NewReportRequest` plus a UI-only telemetry hook, stripped out of
 * `mutationFn` before the request reaches `createNewReport` — never sent to
 * the API. Lets a caller (`useCreateReportForm`) observe success from inside
 * the mutation's own `onSuccess`, the one callback TanStack Query always
 * invokes regardless of whether the calling component is still mounted (see
 * `newReportMutation` below).
 */
type NewReportMutationVariables = NewReportRequest & { onSuccess?: () => void };

/** Same rationale as {@link NewReportMutationVariables}, for the batch flow. */
type BatchReportMutationVariables = BatchReportRequest & { onSuccess?: () => void };

export const useReportCRUD = (folderId?: string | null, enableQuery: boolean = true) => {
  const { closeDialog } = useDialogActions();
  const queryClient = useQueryClient();
  const {
    decryptData,
    _encryptData,
    encryptNgrams,
    encryptPayloadDeterministic,
    deriveKey,
  } = useEncryption();
  const { canViewReportSummary, canCreateReportSummary } = usePermissionManager();
  const { encryptWithEnvelope, generateEnvelope, hasOrgEncryption } = useEnvelopeEncryption();
  const { clearPendingList } = useNewPendingReportsActions();
  const pageNumber = useReportListPage();
  const limitNumber = useReportListLoadCount();
  const searchFilter = useReportListSearchFilter();
  const { setReportList, incrementReportList, resetPagination, setLoadMore, clearSearchFilter, clearFilters, updateItemById } =
    useReportListActions();
  const secretKey = usePassword();
  const isPasswordExpired = useIsPasswordExpired();
  const { withPasswordCheck } = usePasswordCheck();
  const { setUserCredits } = useUserActions();
  const userCredits = useUserCredits();

  const { createDecryptorForItem: createEnvelopeAwareDecryptor } = useEnvelopeAwareDecryptor();
  const isMigrationPending = useIsMigrationPending();

  const reportListQuery = useQuery({
    queryKey: ["reports", "list", folderId],
    queryFn: async ({ meta }) => {
      try {
        clearPendingList();

        let hmacFilters;
        if (searchFilter) {
          console.log("reportListQuery searchFilter", searchFilter);
          const ngrams = await encryptNgrams({
            searched_value: searchFilter,
          });
          hmacFilters = ngrams.searched_value;
          //clearSearchFilter()
        }

        // Read filter/sort state from store (same pattern as searchFilter)
        const { reportType, reportStatus, order, columnOrder, searchScope, favoritesOnly } = useReportListStore.getState();

        const reportsListData = await fetchReportList({
          page: pageNumber,
          limit: limitNumber,
          hmacFilters,
          folderId: folderId,
          reportType,
          reportStatus,
          order,
          columnOrder,
          searchScope,
          favoritesOnly,
        });

        const decryptedItemsList = await Promise.all(
          reportsListData.map(async (item, index) => {
            try {
              const itemDecryptor = createEnvelopeAwareDecryptor(item as any);
              return await decryptListItem(item, itemDecryptor, createEnvelopeAwareDecryptor, deriveKey);
            } catch (error) {
              console.error(`Error decrypting item #${index + 1}:`, error);
              // Return item as-is if decryption fails (e.g. folder encrypted with old key after password reset)
              return item;
            }
          })
        );

        const finalList = decryptedItemsList as ReportData[];

        // Rehydrates progress from persisted status so it survives reload;
        // the store action already no-ops on missing/malformed data.
        for (const item of finalList as any[]) {
          if (item?.user_reports_id) {
            useReportProgressStore
              .getState()
              .actions.rehydrateFromReportStatus(item.user_reports_id, item.report_status);
          }
        }

        //@ts-ignore
        pageNumber === 1 ? setReportList(finalList) : incrementReportList(finalList);

        // Fluxo 2.1 (notes/21367-mapeamento-fluxos-relatorios.html) — só a
        // página 1 de uma busca ativa (não load-more, não navegação sem
        // termo). finalList.length aqui é o resultado JÁ decriptado e
        // resolvido desta resposta específica — nunca o estado de UI
        // (view state), que tem uma janela de um frame onde ainda reflete a
        // lista anterior enquanto a nova busca está em voo.
        if (searchFilter && pageNumber === 1) {
          const resultCount = finalList.length;
          emitEvent({ name: "report_list_search", resultCount });
          if (resultCount === 0) {
            recordZeroResultListSearch(searchFilter);
          }
        }
        // Reconcile favorite overrides: the fetched listing is authoritative ONLY
        // for the items it contains. Overrides for resources not in this listing
        // (e.g. the parent folder you just opened, whose state lives in the parent
        // listing) must persist so their favorite state stays correct.
        const presentFavoriteIds: string[] = [];
        for (const it of finalList as any[]) {
          if (it?.user_reports_id) presentFavoriteIds.push(it.user_reports_id);
          if (it?.folder_id) presentFavoriteIds.push(it.folder_id);
          if (it?.id) presentFavoriteIds.push(it.id);
        }
        useFavoritesStore.getState().actions.clearOverridesFor(presentFavoriteIds);
        const itemCount = finalList.length;
        const hasMore = Boolean(itemCount === limitNumber);
        setLoadMore(hasMore);
        console.log("decrypted items list", finalList);
        // TODO - remover caso não esteja agregando NADA - teste para evitar mensagem de refetch sempre que chamar a lista
        const isScheduledRefetch = meta?.fetchReason === "refetch";
        if (isScheduledRefetch) {
          sessionStorage.removeItem("reports_list_toast_shown");
        }

        return {
          data: finalList as (ReportData | FolderData)[],
          isScheduledRefetch: isScheduledRefetch,
        };
      } catch (err: any) {
        createErrorHandler(
          "Ocorreu um erro ao tentar atualizar a lista de relatórios",
          "Erro ao atualizar lista"
        );

        return {
          data: [],
          isScheduledRefetch: false,
        };
      }
    },
    ...tanstackQueryConfig,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    enabled: !isPasswordExpired && enableQuery && folderId !== undefined && !isMigrationPending,
    meta: {
      fetchReason: "initial",
    },
  });

  const reportDetailsQuery = (id: string) => {
    // Reativo: cair/voltar a conexão recalcula as opções e liga/desliga o fallback sem esperar um novo fetch.
    const isStatusSocketConnected = useIsReportStatusSocketConnected();

    return useQuery<ReportData, Error>({
      queryKey: ["reports", "details", id],
      queryFn: async () => {
        const response = await fetchReportById(id);
        const reportDecryptor = createEnvelopeAwareDecryptor(response as any);
        const decrypted = await decryptReportPayload(response as ReportData, reportDecryptor);
        console.log("[useReportCRUD] reportDetailsQuery decrypted", decrypted);
        // Same rehydration as reportListQuery above.
        useReportProgressStore
          .getState()
          .actions.rehydrateFromReportStatus(id, (decrypted as ReportData)?.report_status);
        return decrypted as ReportData;
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: !!id && !isPasswordExpired,
      // Refetch vem dos terminais do WebSocket; o poll é só fallback (pending E sem socket).
      refetchInterval: (query) => {
        const data = query.state.data;
        return resolveReportDetailsPollMs({
          isPending: !!data && isPendingReport(data),
          isSocketConnected: isStatusSocketConnected,
        });
      },
    });
  };

  const mergeableReportsQuery = (
    reportId: string,
    page: number,
    limit: number,
    searchFilter?: string
  ) => {
    return useQuery({
      queryKey: ["reports", "mergeable", reportId, page, limit, searchFilter],
      queryFn: async () => {
        try {
          let hmacFilters;
          if (searchFilter) {
            console.log("mergeableReportsQuery searchFilter", searchFilter);
            const ngrams = await encryptNgrams({
              searched_value: searchFilter,
            });
            hmacFilters = ngrams.searched_value;
          }

          const response = await fetchMergeableReports({
            report_id: reportId,
            hmacFilters,
            page,
            limit,
          });

          const decryptedMergeableReportsList = await Promise.all(
            response.getReports().map(async (item, index) => {
              try {
                const itemDecryptor = createEnvelopeAwareDecryptor(item as any);
                const decryptedItem = (await decryptListItem(
                  item,
                  itemDecryptor,
                  createEnvelopeAwareDecryptor,
                )) as MergeableReportItem;

                if (
                  decryptedItem.related_documents_encrypted &&
                  Array.isArray(decryptedItem.related_documents_encrypted)
                ) {
                  const decryptedDocuments = await Promise.all(
                    decryptedItem.related_documents_encrypted.map(async (encDoc: any) => {
                      if (typeof encDoc === "object" && "encrypted" in encDoc && "iv" in encDoc) {
                        const decrypted = await decryptData({
                          encrypted: encDoc.encrypted,
                          iv: encDoc.iv,
                        });
                        return decrypted.success ? decrypted.data : null;
                      }
                      return encDoc;
                    })
                  );
                  decryptedItem.related_documents_encrypted = decryptedDocuments.filter(Boolean);
                }

                return decryptedItem;
              } catch (error) {
                console.error(`Error decrypting item #${index + 1}:`, error);
                throw error;
              }
            })
          );

          return {
            data: decryptedMergeableReportsList,
            pagination: response.getPagination(),
          };

          // // mock
          // const mockResponse = await fetch("/mock_lista_reports_combinaveis.json");
          // const allMockData = await mockResponse.json();
          // const startIndex = (page - 1) * limit;
          // const endIndex = startIndex + limit;
          // const paginatedData = allMockData.data.slice(startIndex, endIndex);
          // const mockPaginatedResponse = {
          //   data: paginatedData,
          //   pagination: {
          //     ...allMockData.pagination,
          //     current_page: page,
          //     page_size: limit,
          //     has_next: endIndex < allMockData.data.length,
          //     has_previous: page > 1,
          //   },
          // };

          // const mockDTO = new MergeableReportsOutputDTO(mockPaginatedResponse);

          // return {
          //   data: mockPaginatedResponse.data,
          //   pagination: mockDTO.getPagination(),
          // };
        } catch (err: any) {
          console.error("Error fetching mergeable reports:", err);
          throw err;
        }
      },
      ...tanstackQueryConfig,
      staleTime: Infinity,
      enabled: !!reportId && !isPasswordExpired,
    });
  };

  const newReportMutation = useMutation({
    mutationFn: async (report: NewReportMutationVariables) => {
      try {
        const ngrams = await encryptNgrams({
          report_search_args: buildReportSearchArgsNgramInput(
            report.report_type,
            report.report_search_args,
          ),
        });

        // `onSuccess` is a UI-only telemetry hook (see `NewReportMutationVariables`)
        // — strip it here same as `report_search_args`, so it never reaches
        // the API payload.
        const { report_search_args, onSuccess: _onSuccess, ...newReport } = report;

        const payload = {
          ...newReport,
          [REPORT_CONSTANTS.new_report.hmac]: {
            [REPORT_CONSTANTS.new_report.ngrams]: ngrams,
          },
        };

        return createNewReport(payload);
      } catch (error) {
        console.error("Error creating new report in SNAP API:", error);
        throw new Error("Failed to create new report in SNAP API");
      }
    },
    onSuccess: async (data: { id: string; credits_minimun: number }, variables) => {
      closeDialog();
      setUserCredits(data?.credits_minimun);
      toast.success("Gerando novo relatório...");
      // Task 5.14: marca o início da latência percebida create→ready. Reusa o
      // mesmo id que o terminal `completed` trará (task 5.15).
      recordCreateIssued(data?.id);
      window.dispatchEvent(new Event("snap:report-created"));
      withPasswordCheck(() => {
        const targetFolder = variables.parent_folder_id ?? null;
        const currentFolder = folderId ?? null;
        invalidateCurrentFolderNoFilters(targetFolder);
        if (targetFolder !== currentFolder) {
          invalidateCurrentFolderNoFilters(currentFolder);
        }
        // Retry reuses the same id — refresh its details cache too, so a
        // details page open on it picks up "pending" right away instead of
        // waiting for the next terminal WS message.
        if (variables.user_reports_id) {
          invalidateReportDetails(variables.user_reports_id);
        }
      });
      // Telemetria do funil (`useCreateReportDialogButtons`): tem que ser a
      // opção de MUTAÇÃO (`this.options.onSuccess`, sempre executa), não a
      // opção por chamada de `mutateAsync`/`mutate` — essa passa por
      // `MutationObserver#notify`, que só dispara `if (this.hasListeners())`,
      // e `closeDialog()` acima já pode ter desmontado o observer.
      variables.onSuccess?.();
    },
    onError: createErrorHandler(
      "Ocorreu um erro ao tentar criar um novo relatório",
      "Erro ao criar relatório"
    ),
  });

  // Refaz o relatório reaproveitando o mesmo user_reports_id; criptografia fica no hook (fonte única).
  const retryReport = useCallback(async (report: {
    id: string;
    reportType: string;
    searchArgs: Record<string, unknown>;
    parentFolderId?: string | null;
  }) => {
    const isRelacoesType = report.reportType === REPORT_CONSTANTS.types.relacoes;

    let plainTextValue: unknown;
    let searchArgsOnly: unknown;

    if (isRedesSociaisType(report.reportType)) {
      // redes_sociais persiste os search args na DISPLAY shape
      // ({ redes_sociais, redes_sociais_ignore }). Mantém o objeto inteiro em
      // report_search_args para o input de n-grams indexar o valor de entrada +
      // network/kind; colapsar para string quebra a extração de n-grams no retry.
      const entry = extractRedesSociaisEntryValue(report.searchArgs);
      if (!entry) {
        // Sem a entrada original o dispatch iria com input vazio e o retry
        // nasceria quebrado — falhar aqui é o comportamento honesto.
        throw new Error(
          "Não foi possível identificar a entrada original deste relatório (URL ou identificador). Crie um novo relatório com a entrada desejada.",
        );
      }
      const { network } = extractRedesSociaisSafeFields(report.searchArgs);
      const ignore = (report.searchArgs as Record<string, unknown>)[REDES_SOCIAIS_IGNORE_KEY];
      const inputMode =
        ignore && typeof ignore === "object" && !Array.isArray(ignore) &&
          (ignore as Record<string, unknown>).input_mode === "identifier"
          ? "identifier"
          : "url";

      plainTextValue =
        inputMode === "identifier"
          ? { identifier: entry, network, entry_kind: "perfil" }
          : entry;
      searchArgsOnly = report.searchArgs;
    } else if (report.reportType === "veiculo") {
      const veiculoArgs = report.searchArgs as Record<string, unknown>;
      if ("placa" in veiculoArgs || "chassi" in veiculoArgs) {
        // Manual two-field flow ({"placa": ..., "chassi": ...}): both fields
        // must be resent together — Snap dev only resolves reliably when
        // both are present in the same request (confirmed live 2026-09-03).
        // The generic "first value only" branch below would silently drop
        // the chassi (or placa) and resend a single-field request that
        // Snap's internal cross-reference can't reliably resolve alone.
        plainTextValue = veiculoArgs;
      } else {
        // Legacy single-field shape from AUTO/batch mode: {"veiculo": "<value>"}.
        plainTextValue = veiculoArgs.veiculo as string;
      }
      searchArgsOnly = plainTextValue;
    } else {
      const searchArgs = report.searchArgs as Record<string, string[]>;
      const firstValue = Object.values(searchArgs)[0];
      const valueString = Array.isArray(firstValue) ? firstValue[0] : firstValue;
      plainTextValue = isRelacoesType ? searchArgs : valueString;

      searchArgsOnly = plainTextValue;
      if (isRelacoesType && typeof searchArgs === "object") {
        const { ignore_consortiums, ignore_other_contacts, ...searchArgsWithoutFlags } = searchArgs;
        searchArgsOnly = searchArgsWithoutFlags;
      }
    }

    const { encrypted, envelope } = await encryptWithEnvelope(report.searchArgs);
    if (!encrypted.data) {
      throw new Error("Erro ao criptografar dados do relatório");
    }

    await newReportMutation.mutateAsync({
      report_type: report.reportType,
      report_input_value: plainTextValue,
      report_search_args: searchArgsOnly,
      report_input_encrypted: encrypted.data,
      user_reports_id: report.id,
      parent_folder_id: report.parentFolderId,
      ...envelope,
    } as NewReportRequest);
  }, [encryptWithEnvelope, newReportMutation]);

  /** Search args persisted for a report, from the cheapest source that has them:
   * the loaded list, the details cache, then the server (decrypted). */
  const resolvePersistedSearchArgs = useCallback(async (userReportsId: string): Promise<unknown> => {
    const fromList = useReportListStore
      .getState()
      .list.find((item) => item?.user_reports_id === userReportsId)?.report_search_args;
    if (extractRedesSociaisEntryValue(fromList)) return fromList;

    const cached = queryClient.getQueryData<ReportData>(["reports", "details", userReportsId]);
    if (extractRedesSociaisEntryValue(cached?.report_search_args)) {
      return cached?.report_search_args;
    }

    // The list can be scoped to another folder and the details page may never
    // have been opened — the persisted row is the only remaining source.
    try {
      const response = await fetchReportById(userReportsId);
      const decryptor = createEnvelopeAwareDecryptor(response as never);
      const decrypted = await decryptReportPayload(response as ReportData, decryptor);
      return (decrypted as ReportData)?.report_search_args;
    } catch (error) {
      console.warn("[useReportCRUD] Falha ao recuperar search args persistidos", error);
      return fromList ?? cached?.report_search_args;
    }
  }, [queryClient, createEnvelopeAwareDecryptor]);

  const addNewReportMutation = useMutation({
    mutationFn: async (report: any) => {
      try {
        const { user_reports_id } = report;
        if (user_reports_id) {
          // Recover the redes_sociais entry from what creation persisted and
          // rebuild name/args before re-encrypting, for when the completion
          // envelope arrives without it (cross-worker name-cache miss).
          const patch = reconcileRedesSociaisCompletion(
            report,
            extractRedesSociaisFallback(await resolvePersistedSearchArgs(user_reports_id)),
          );
          if (patch.report_name) report.report_name = patch.report_name;
          if (patch.report_search_args) report.report_search_args = patch.report_search_args;

          // Still unknown: omit both fields so the PUT preserves what the DB
          // already has instead of blanking the entry for good.
          const isUnresolvedRedesSociais =
            isRedesSociaisType(report.report_type) &&
            !extractRedesSociaisEntryValue(report.report_search_args);
          if (isUnresolvedRedesSociais) {
            console.warn(
              `[useReportCRUD] redes_sociais sem entrada resolvida (report=${user_reports_id}) — ` +
              "report_name/report_search_args preservados como estão no banco.",
            );
            delete report.report_search_args;
            delete report.report_name;
          }

          const uniquePNames = report?.subject_multiple_p_names?.length
            ? [...new Set(report.subject_multiple_p_names)]
            : undefined;
          const uniqueCNames = report?.subject_multiple_c_names?.length
            ? [...new Set(report.subject_multiple_c_names)]
            : undefined;

          const ngrams = await encryptNgrams({
            report_name: report.report_name,
            // redes_sociais: flatten to the plaintext entry value (handle/url) +
            // safe network/kind so the report is searchable by its input, like
            // every other type indexes its value. Skips the encrypted-url
            // envelope. No-op for every other report type.
            report_search_args: buildReportSearchArgsNgramInput(
              report.report_type,
              report.report_search_args,
            ),
            subject_name: report.subject_name,
            subject_mother_name: report.subject_mother_name,
            ...(report?.subject_name_related && {
              subject_name_related: report.subject_name_related,
            }),
            ...(uniquePNames && {
              subject_multiple_p_names: uniquePNames,
            }),
            ...(uniqueCNames && {
              subject_multiple_c_names: uniqueCNames,
            }),
          });
          console.log("[ useReportCRUD ] report", report);

          /* não enviar mais created_at, modified_at, related_documents */
          const { created_at, modified_at, related_documents, __optimisticLock, ...newReport } = report;
          const modifiedReports = {
            ...newReport,
            // Structured object, not a bare string — matches what the backend
            // and reportStatus.helper.ts both expect.
            [REPORT_CONSTANTS.new_report.report_status]: {
              [REPORT_CONSTANTS.new_report.status_report]: REPORT_CONSTANTS.status.completed,
            },
          };

          let encryptedReport: ReportData;

          // Org users: encrypt fields with per-report DEK, wrap DEK for both user and org paths
          // Standalone users: encrypt fields directly with user's key (legacy path, unchanged)
          const { dek, envelope: envelopeFields } = await generateEnvelope();
          if (dek) {
            encryptedReport = await encryptReportPayload(
              modifiedReports,
              null,
              async (data: any, _ignored: ArrayBuffer) => _encryptData(data, dek.buffer as ArrayBuffer),
            );
          } else {
            encryptedReport = await encryptReportPayload(
              modifiedReports,
              secretKey,
              _encryptData
            );
          }

          let relatedDocumentsEncryptedList;
          if (report?.related_documents) {
            const derivedKey = base64ToUint8Array(secretKey || "");
            relatedDocumentsEncryptedList = await Promise.all(
              report.related_documents.map(async (doc: string) => {
                const normalized = normalizeDocument(doc);
                const hmacHash = await encryptPayloadDeterministic([normalized]);
                const encrypted = await _encryptData(normalized, derivedKey.buffer);

                if (!encrypted.success || !encrypted.data) {
                  throw new Error("Failed to encrypt related document");
                }

                return {
                  related_documents: hmacHash[0],
                  encrypted: encrypted.data.encrypted,
                  iv: encrypted.data.iv,
                };
              })
            );
          }

          // Adicionar report_ids_used sem criptografar
          if (
            report?.report_sources_used &&
            encryptedReport[REPORT_CONSTANTS.new_report.report_sources_used]
          ) {
            const reportIdsUsed = report.report_sources_used.map(
              (source: any) => source.user_reports_id
            );
            const encryptedSourcesUsed = encryptedReport[
              REPORT_CONSTANTS.new_report.report_sources_used
            ] as any;
            encryptedReport[REPORT_CONSTANTS.new_report.report_sources_used] = {
              encrypted: encryptedSourcesUsed.encrypted,
              iv: encryptedSourcesUsed.iv,
              report_ids_used: reportIdsUsed,
            };
          }

          const payload = {
            ...encryptedReport,
            [REPORT_CONSTANTS.new_report.related_documents_encrypted]:
              relatedDocumentsEncryptedList,
            [REPORT_CONSTANTS.new_report.hmac]: {
              [REPORT_CONSTANTS.new_report.ngrams]: ngrams,
            },
            ...(envelopeFields.user_wrapped_dek ? envelopeFields : {}),
          };

          // Guard de concorrência otimista (#21909): só o save MANUAL de
          // detalhes marca `__optimisticLock` (ver useReportActionsWithAutoSave).
          // Nesse caso enviamos o `modified_at` que a aba carregou como header
          // base para o backend detectar edição concorrente (409). O fluxo de
          // conclusão/streaming não marca o flag, então a cerca fica inativa
          // para ele — mantém o comportamento legado intacto.
          const optimisticBase =
            __optimisticLock && typeof modified_at === "string" ? modified_at : undefined;

          return addNewReport(payload, user_reports_id, optimisticBase);
        }
      } catch (error) {
        console.error("Error creating new report:", error);
        throw new Error("Failed to create new report");
      }
    },
  });

  const mergeReportsMutation = useMutation({
    mutationFn: async (payload: MergeReportsProps) => {
      const { reportsSelectedIdList, parent_folder_id, related_documents } = payload;

      if (!reportsSelectedIdList || reportsSelectedIdList.length === 0) {
        throw new Error("No reports selected for merging");
      }

      try {
        const reportsDetails = await Promise.all(
          reportsSelectedIdList.map(async (reportId) => {
            const report = await fetchReportById(reportId);
            if (!report || typeof report !== 'object') {
              throw new Error(`Failed to fetch report with ID: ${reportId}`);
            }
            const reportDecryptor = createEnvelopeAwareDecryptor(report as any);
            return decryptReportPayload(report as ReportData, reportDecryptor);
          })
        ) as ReportData[];

        if (!reportsDetails || reportsDetails.length === 0) {
          throw new Error("Failed to fetch report details");
        }

        const reportSearchArgs = combineReportSearchArgs(reportsDetails);
        const relatedDocumentSearchArgs = {
          [REPORT_CONSTANTS.new_report.related_documents]: related_documents,
        };
        const combinedSearchArgs = {
          ...reportSearchArgs,
          ...relatedDocumentSearchArgs,
        };

        // Combina subject_multiple_p_names e subject_multiple_c_names de todos os relatórios sem duplicatas
        const allMultiplePNames = [...new Set(
          reportsDetails
            .flatMap((r: any) => r?.subject_multiple_p_names || [])
            .filter(Boolean)
        )];
        const allMultipleCNames = [...new Set(
          reportsDetails
            .flatMap((r: any) => r?.subject_multiple_c_names || [])
            .filter(Boolean)
        )];

        const ngrams = await encryptNgrams({
          report_search_args: combinedSearchArgs,
          ...(allMultiplePNames.length && { subject_multiple_p_names: allMultiplePNames }),
          ...(allMultipleCNames.length && { subject_multiple_c_names: allMultipleCNames }),
        });

        const { encrypted: encryptedSearchArgs, envelope } = await encryptWithEnvelope(combinedSearchArgs);

        const encryptedRelatedDocuments = await encryptPayloadDeterministic([related_documents]);

        const mergeReportsPayload = {
          [REPORT_CONSTANTS.new_report.report_type]: REPORT_CONSTANTS.types.combinado,
          [REPORT_CONSTANTS.new_report.related_documents]: related_documents,
          [REPORT_CONSTANTS.new_report.report_input_encrypted]: encryptedSearchArgs.data,
          [REPORT_CONSTANTS.new_report.related_documents_encrypted]: encryptedRelatedDocuments[0],
          [REPORT_CONSTANTS.new_report.parent_folder_id]: parent_folder_id,
          [REPORT_CONSTANTS.new_report.hmac]: {
            [REPORT_CONSTANTS.new_report.ngrams]: ngrams,
          },
          [REPORT_CONSTANTS.merge_reports.reports_to_merge]: reportsDetails,
          ...envelope,
        };

        console.log("[ useReportCRUD ] mergeReportsPayload", mergeReportsPayload);

        const mergeResult = await mergeReports(mergeReportsPayload as MergeReportsPayload) as {
          user_reports_id: string;
          report_type: string;
          report_name: string;
          subject_name?: string;
          subject_mother_name?: string;
          subject_age?: string;
          subject_sex?: string;
        };

        // The backend only creates the row as "pending" and computes
        // report_name/subject_* transiently (zero-knowledge: it never
        // persists plaintext) — it hands them back so the client can encrypt
        // them here, same as every other report type's finalization, and PUT
        // via addNewReportMutation (insert-report). Without this the report
        // stayed "pending" forever (data/report_status never left the
        // placeholder written at creation) — found live 2026-09-08.
        //
        // `data`/`report_sources_used` are built HERE instead of taken from
        // the backend response: `reportsDetails` (already decrypted, above)
        // has everything needed, and the shape every combinado consumer
        // actually expects (`combined_data`/`report_sources_data` — see
        // combineReportSections' docstring) was never built anywhere,
        // backend included — a merged report rendered as completely empty
        // once it actually finished, the SAME missing-content bug wearing a
        // different symptom.
        const { combinedData, reportSourcesData } = combineReportSections(reportsDetails);
        const reportSourcesUsed = buildReportSourcesUsed(reportsDetails);

        await addNewReportMutation.mutateAsync({
          user_reports_id: mergeResult.user_reports_id,
          report_type: mergeResult.report_type,
          report_name: mergeResult.report_name,
          subject_name: mergeResult.subject_name,
          subject_mother_name: mergeResult.subject_mother_name,
          subject_age: mergeResult.subject_age,
          subject_sex: mergeResult.subject_sex,
          data: {
            [REPORT_CONSTANTS.types.combinado]: [
              { combined_data: combinedData, report_sources_data: reportSourcesData },
            ],
          },
          report_sources_used: reportSourcesUsed,
          report_search_args: combinedSearchArgs,
          // Without this the combinado gets no report_merges row and never shows up as mergeable.
          related_documents: [related_documents],
        });

        return mergeResult;
      } catch (error) {
        console.error("Error merging reports:", error);
        throw new Error("Failed to merge reports");
      }
    },
  });

  const deleteReportMutation = useMutation({
    mutationFn: async (reportId: string) => {
      try {
        await deleteReport(reportId);
      } catch (error) {
        console.error("Error deleting report:", error);
        throw error;
      }
    },
    onSuccess: async (data: any) => {
      closeDialog();
      toast.success(`Relatório deletado com sucesso!`);
      queryClient.removeQueries({
        queryKey: ["reports", "mergeable"],
      });
      withPasswordCheck(() => {
        invalidateCurrentFolder(folderId || null);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar deletar o relatório",
        "Erro ao deletar relatório"
      )(error);
    },
  });

  const deleteBatchMutation = useMutation({
    mutationFn: async (batchId: string) => {
      try {
        await deleteBatch(batchId);
      } catch (error) {
        console.error("Error deleting batch:", error);
        throw error;
      }
    },
    onSuccess: async (data: any) => {
      closeDialog();
      toast.success(`Lote deletado com sucesso!`);
      queryClient.removeQueries({
        queryKey: ["reports", "mergeable"],
      });
      withPasswordCheck(() => {
        invalidateCurrentFolder(folderId || null);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar deletar o lote",
        "Erro ao deletar lote"
      )(error);
    },
  });

  const invalidateToInitialPage = async () => {
    await resetPagination();
    await queryClient.invalidateQueries({
      queryKey: ["reports", "list", folderId || null],
      exact: true,
    });
  };

  const invalidateReportDetails = async (id: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["reports", "details", id],
      exact: true,
    });
  };

  const setReportDetailsToStale = async (id: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["reports", "details", id],
      exact: true,
      refetchType: "none", // Marca como stale mas não faz refetch imediatamente
    });
  };

  // resetQueries remove os dados do cache forçando refetch na próxima renderização
  const resetReportListQuery = async () => {
    await queryClient.resetQueries({
      queryKey: ["reports", "list"],
      exact: true,
    });
  };

  const invalidateCurrentFolder = (targetFolderId: string | null) => {
    queryClient.invalidateQueries({
      queryKey: ["reports", "list", targetFolderId],
      exact: true,
    });
  };

  const invalidateAllReports = () => {
    // Returns the invalidation promise so callers (e.g. the load-more flow in
    // ReportsList) can await the refetch settling before acting on the new data.
    // invalidateQueries resolves once the active refetches it triggered complete.
    return queryClient.invalidateQueries({
      queryKey: ["reports", "list"],
      exact: false,
    });
  };

  const invalidateCurrentFolderNoFilters = async (targetFolderId: string | null) => {
    await resetPagination();
    await clearSearchFilter(true);
    clearFilters();
    await queryClient.invalidateQueries({
      queryKey: ["reports", "list", targetFolderId],
      exact: true,
    });
  };

  const generatePDFMutation = useMutation({
    mutationFn: async (props: ReportPDFProps) => {
      try {
        return await pdfGateway.generatePDF(props);
      } catch (error) {
        console.error("Error generating PDF:", error);
        throw error;
      }
    },
    onError: (error: Error) => {
      console.error("Error generating PDF:", error);
    },
  });

  const generateDOCXMutation = useMutation({
    mutationFn: async (props: ReportPDFProps) => {
      try {
        return await docxGateway.generateDOCX(props);
      } catch (error) {
        console.error("Error generating DOCX:", error);
        throw error;
      }
    },
    onError: (error: Error) => {
      console.error("Error generating DOCX:", error);
    },
  });

  const renameReportMutation = useMutation({
    mutationFn: async ({ report_id, report_name, user_wrapped_dek, user_dek_iv }: {
      report_id: string;
      report_name: string;
      user_wrapped_dek?: string | null;
      user_dek_iv?: string | null;
    }) => {
      try {
        const hmacResult = await encryptNgrams({
          report_name: report_name,
        });

        let encryptedReportName;

        if (user_wrapped_dek && user_dek_iv) {
          // Envelope path: unwrap the existing per-report DEK and encrypt the new name with it.
          // Other fields (report_status, etc.) share this same DEK, so we must NOT replace it.
          const userDek = base64ToUint8Array(secretKey || "");
          const rawUserKey = userDek.buffer.slice(userDek.byteOffset, userDek.byteOffset + userDek.byteLength) as ArrayBuffer;
          const existingDek = await unwrapDekFromUser(rawUserKey, user_wrapped_dek, user_dek_iv);
          encryptedReportName = await _encryptData(report_name, existingDek.buffer as ArrayBuffer);
        } else {
          // Legacy path: encrypt directly with user's derived key
          const derivedKey = base64ToUint8Array(secretKey || "");
          encryptedReportName = await _encryptData(report_name, derivedKey.buffer);
        }

        if (!encryptedReportName?.success || !encryptedReportName.data) {
          throw new Error("Erro ao criptografar nome do relatório");
        }

        await renameReport({
          [REPORT_CONSTANTS.new_report.report_id]: report_id,
          [REPORT_CONSTANTS.new_report.report_name]: encryptedReportName.data as EncryptedPayload,
          [REPORT_CONSTANTS.new_report.hmac]: {
            [REPORT_CONSTANTS.new_report.ngrams]: hmacResult,
          },
        });
      } catch (error) {
        console.error("Error renaming report:", error);
        throw error;
      }
    },
    onSuccess: async (data: any) => {
      closeDialog();
      toast.success(`Relatório renomeado com sucesso!`);
      withPasswordCheck(() => {
        invalidateCurrentFolderNoFilters(folderId || null);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar renomear o relatório",
        "Erro ao renomear relatório"
      )(error);
    },
  });

  const batchReportMutation = useMutation({
    mutationFn: async (payload: BatchReportMutationVariables) => {
      try {
        // `onSuccess` é o hook de telemetria (ver `BatchReportMutationVariables`)
        // — nunca vai pro backend. `createBatchReport` recebe só o resto.
        const { onSuccess: _onSuccess, ...batchPayload } = payload;

        // Envelope do header do batch; retries mantêm o envelope já enviado.
        if (hasOrgEncryption && !batchPayload.user_wrapped_dek) {
          const { envelope } = await generateEnvelope();
          Object.assign(batchPayload, envelope);
        }

        return await createBatchReport(batchPayload);
      } catch (error) {
        console.error("Error creating batch report:", error);
        throw error;
      }
    },
    onSuccess: async (data: any, variables) => {
      closeDialog();
      toast.success(variables.batch_id ? "Refazendo relatórios do lote..." : "Gerando novos relatórios em lote...");
      // #21290: o badge de créditos do header não refletia o consumo de um lote.
      // /batch-create despacha o processamento em background e nunca devolve o
      // saldo pós-débito na resposta síncrona (ao contrário do /create de
      // relatório único, que retorna `credits_minimun` e já atualiza o badge).
      // Decremento otimista: 1 entrada = 1 crédito, mesma premissa já usada em
      // MultipleReportsInputStep (`hasExceededLimit = lineCount > userCredits`).
      setUserCredits(Math.max(0, userCredits - variables.report_entries.length));
      window.dispatchEvent(new Event("snap:report-created"));
      withPasswordCheck(() => {
        const targetFolder = variables.parent_folder_id ?? null;
        const currentFolder = folderId ?? null;
        invalidateCurrentFolderNoFilters(targetFolder);
        if (targetFolder !== currentFolder) {
          invalidateCurrentFolderNoFilters(currentFolder);
        }
      });
      // Telemetria do funil — ver o comentário equivalente em `newReportMutation`.
      variables.onSuccess?.();
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar criar o lote de relatórios",
        "Erro ao criar lote de relatórios"
      )(error);
    },
  });

  const generateSummaryMutation = useMutation({
    mutationFn: async ({
      report_id,
      data,
      user_prompt,
      ai_model,
      report_sources_data,
      report_sources_used,
    }: {
      report_id: string;
      data: any;
      user_prompt?: string;
      ai_model?: string;
      report_sources_data?: any[];
      report_sources_used?: any[];
    }) => {
      if (!canCreateReportSummary()) {
        throw new Error("Você não tem permissão para gerar sumário executivo");
      }
      try {
        // Envelope já na criação para o pending/erro (que não recebe PUT) não virar falso "item legado".
        const { envelope } = await generateEnvelope();

        const response = await generateSummaryRequest({
          report_id,
          data,
          ai_model: ai_model ?? getUserAiModel(),
          user_prompt: user_prompt ?? "",
          report_sources_data,
          report_sources_used,
          ...envelope,
        });
        return { response, report_id };
      } catch (error) {
        console.error("Error generating resume:", error);
        throw error;
      }
    },
    onSuccess: async (data) => {
      toast.success("Gerando sumário executivo...");
      window.dispatchEvent(new Event("snap:report-created"));

      // Atualização otimista: marca o relatório como "gerando sumário"
      const fields = { has_summary: true, summary_generating: true } as any;
      updateItemById(data.report_id, fields);
      queryClient.setQueriesData<any>(
        { queryKey: ["reports", "list"], exact: false },
        (oldData: any) => {
          if (!oldData?.data) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item: any) => {
              if (item?.user_reports_id === data.report_id) {
                return { ...item, ...fields };
              }
              return item;
            }),
          };
        }
      );

      withPasswordCheck(() => {
        invalidateSummary(data.report_id);
      });
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar gerar o sumário executivo",
        "Erro ao gerar sumário",
      )(error);
    },
  });

  const updateResumeMutation = useMutation({
    mutationFn: async ({
      reportId,
      summaryPayload,
      report,
    }: {
      reportId: string;
      summaryPayload: SummaryResponse;
      report?: { user_wrapped_dek?: string | null; user_dek_iv?: string | null };
    }) => {
      if (!canCreateReportSummary()) {
        throw new Error("Você não tem permissão para atualizar sumário executivo");
      }
      try {
        let encryptedSummary: ReportData;
        const { dek, envelope: envelopeFields } = await generateEnvelope();

        if (dek) {
          encryptedSummary = await encryptReportPayload(
            summaryPayload,
            null,
            async (data: any, _ignored: ArrayBuffer) => _encryptData(data, dek.buffer as ArrayBuffer),
          );
        } else {
          encryptedSummary = await encryptReportPayload(summaryPayload, secretKey, _encryptData);
        }

        if (!encryptedSummary) {
          throw new Error("Erro ao criptografar sumário");
        }
        const payload = { ...encryptedSummary, ...envelopeFields } as unknown as UpdateSummaryInput;
        await updateSummaryRequest(reportId, payload);
      } catch (error) {
        console.error("Error updating resume:", error);
        throw error;
      }
    },
    onError: (error: Error) => {
      createErrorHandler(
        "Ocorreu um erro ao tentar salvar o resumo",
        "Erro ao salvar resumo",
      )(error);
    },
  });

  const getSummaryQuery = (reportId: string, report?: { user_wrapped_dek?: string | null; user_dek_iv?: string | null }) =>
    useQuery({
      queryKey: ["reports", "summary", reportId],
      queryFn: async () => {
        try {
          const summaryDTO = await getSummaryRequest(reportId);
          if (!summaryDTO) {
            return null;
          }

          const summaryModel = summaryDTO.getSummaryModel();
          const rawData = summaryModel.raw.data;

          if (
            rawData &&
            typeof rawData === "object" &&
            "encrypted" in rawData
          ) {
            // Use the summary's own envelope fields for decryption
            const summaryDecryptor = createEnvelopeAwareDecryptor(summaryModel.raw as any);
            const decrypted = await summaryDecryptor(rawData);
            if (decrypted.success && decrypted.data) {
              const decryptedResponse = {
                ...summaryModel.raw,
                data: decrypted.data,
              };
              return new SummaryOutputDTO(decryptedResponse);
            }
          }

          return summaryDTO;
        } catch (error: any) {
          // 404 significa que o sumário não existe ainda
          if (error?.response?.status === 404) {
            return null;
          }
          console.error("Error fetching summary:", error);
          throw error;
        }
      },
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!reportId && !isPasswordExpired && canViewReportSummary(),
      // Poll every 30s while the summary is "pending" to detect
      // stale-to-error transitions when the WS event is lost.
      refetchInterval: (query) => {
        const data = query.state.data;
        const status = data?.getStatus?.();
        return status === "pending" ? 30_000 : false;
      },
    });

  const invalidateSummary = async (reportId: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["reports", "summary", reportId],
      exact: true,
    });
  };

  return {
    reportListQuery,
    reportDetailsQuery,
    mergeableReportsQuery,
    invalidateToInitialPage,
    invalidateReportDetails,
    setReportDetailsToStale,
    resetReportListQuery,
    invalidateCurrentFolder,
    invalidateAllReports,
    invalidateCurrentFolderNoFilters,
    newReportMutation,
    retryReport,
    addNewReportMutation,
    deleteReportMutation,
    deleteBatchMutation,
    generatePDFMutation,
    generateDOCXMutation,
    renameReportMutation,
    mergeReportsMutation,
    batchReportMutation,
    generateSummaryMutation,
    updateResumeMutation,
    getSummaryQuery,
    invalidateSummary,
  };
};
